import type { SVGProps } from 'react';

/**
 * The Dawn Patrol logo, taken from dawnpatrol.cloud and self-hosted rather than
 * hotlinked. The wave mark keeps its brand colours; the wordmark is drawn with
 * `currentColor`, so it takes the text colour of wherever it is placed.
 *
 * `role="img"` + `aria-label` is the correct pattern for an inline SVG — it is
 * announced as one image rather than as 22 unlabelled paths. oxlint's
 * `prefer-tag-over-role` doesn't know about `<svg>`, so it's disabled for this
 * file in `.oxlintrc.json`.
 */
export function DawnPatrolLogo(props: SVGProps<SVGSVGElement>) {
    return (
        <svg
            viewBox="0 0 149 44"
            fill="none"
            role="img"
            aria-label="Dawn Patrol"
            {...props}
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M22.1425 0.960449C15.494 0.960449 9.60894 4.03406 5.98755 8.75551H38.2975C34.6761 4.03406 28.7911 0.960449 22.1425 0.960449Z"
                fill="#FF9D5A"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.59868 30.9866C2.50638 30.7872 41.2914 30.9866 41.2914 30.9866C41.2914 30.9866 35.7081 43.1554 22.4349 43.1554C9.16175 43.1554 4.13734 34.3099 2.59868 30.9866Z"
                fill="#02A9B1"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.74352 30.22H28.0215C28.0215 30.22 27.4626 23.8886 34.4002 21.4082L0.800805 21.8022C0.800805 21.8022 0.537382 26.8666 2.74352 30.22Z"
                fill="#03949C"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M42.8729 22.1426H34.9605C33.8052 22.1426 27.9606 24.4658 27.9606 30.333H41.2826C41.2826 30.333 42.8729 25.3455 42.8729 22.1426Z"
                fill="#048C95"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.508179 21.5215H17.9279C17.9279 21.5215 18.5407 15.6521 23.1026 14.5737H2.10343L0.508179 21.5215Z"
                fill="#076C75"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M43.0989 21.1821H18.6405C18.6405 21.1821 19.4624 14.7427 25.066 14.7427H41.8025L43.0989 21.1821Z"
                fill="#085A64"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.31592 14.2908H8.49048L10.7323 9.0376H5.25982L2.31592 14.2908Z"
                fill="#0B4B55"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M38.6465 9.0376H12.6094C12.6094 9.0376 9.33603 10.0609 8.58582 14.2908H42.3644L38.6465 9.0376Z"
                fill="#0D3640"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M22.0013 42.2563C14.2773 42.2563 7.54408 37.9459 4.09226 31.6104H39.9101C36.4585 37.9459 29.7253 42.2563 22.0013 42.2563ZM22.0014 1.63341C27.9344 1.63341 33.2823 4.17734 37.008 8.22804H6.99457C10.7203 4.17734 16.0683 1.63341 22.0014 1.63341ZM36.9716 20.7616V20.7604C36.9559 20.7604 36.9403 20.7614 36.9244 20.7616H19.1729C19.5754 17.5997 22.2836 15.1466 25.5564 15.1466V15.1454H41.1925C41.8257 16.918 42.2187 18.8028 42.3321 20.7616H36.9716ZM36.9244 22.395H42.362C42.3029 25.0823 41.7177 27.6421 40.7058 29.977H28.6031C29.0141 25.7426 32.5849 22.4191 36.9244 22.395ZM20.6941 15.1454C18.9501 16.4643 17.7563 18.4729 17.5254 20.7616H1.67038C1.78382 18.8028 2.17684 16.918 2.81002 15.1454H20.6941ZM8.06643 13.5121H3.47518C4.07054 12.2167 4.79815 10.9939 5.64143 9.86141H9.96596C8.95304 10.8008 8.25998 12.0781 8.06643 13.5121ZM40.5275 13.5121H9.7215C10.1074 11.439 11.9303 9.86406 14.1157 9.86406V9.86141H38.3612C39.2045 10.9939 39.9323 12.2167 40.5275 13.5121ZM1.6404 22.395H31.4866C28.959 24.0476 27.2209 26.8057 26.9636 29.977H3.29652C2.28462 27.6421 1.69947 25.0823 1.6404 22.395ZM22.0014 0C9.86985 0 0 9.84437 0 21.9448C0 34.045 9.86985 43.8896 22.0014 43.8896C34.1329 43.8896 44.0026 34.045 44.0026 21.9448C44.0026 9.84437 34.1329 0 22.0014 0Z"
                fill="white"
            />
            <mask
                id="mask0_dp"
                style={{ maskType: 'luminance' }}
                maskUnits="userSpaceOnUse"
                x="58"
                y="16"
                width="7"
                height="12"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M58 16.1714H64.7242V27.0667H58V16.1714Z"
                    fill="white"
                />
            </mask>
            <g mask="url(#mask0_dp)">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M62.707 21.5742C62.707 19.1398 62.0561 17.9226 60.7546 17.9226H59.9667V25.293H60.603C61.3212 25.293 61.8512 24.9837 62.1937 24.3652C62.5358 23.7465 62.707 22.8163 62.707 21.5742ZM64.7242 21.5147C64.7242 23.3132 64.3686 24.6882 63.6576 25.6396C62.9468 26.5913 61.9234 27.0667 60.5886 27.0667H57.9999V16.1714H60.8271C62.0559 16.1714 63.0129 16.6394 63.6975 17.5761C64.3817 18.5125 64.7242 19.8255 64.7242 21.5147Z"
                    fill="currentColor"
                />
            </g>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M71.18 22.521L70.6158 19.9871C70.5821 19.833 70.5062 19.4466 70.3883 18.8282C70.2701 18.2095 70.1989 17.7958 70.1748 17.5874C70.1122 18.0146 70.0324 18.4854 69.9363 18.9995C69.8398 19.5139 69.5891 20.6875 69.1844 22.521H71.18ZM72.1561 27.0672L71.5487 24.3173H68.7938L68.1864 27.0672H66.22L68.8373 16.1265H71.5055L74.1299 27.0672H72.1561Z"
                fill="currentColor"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M84.4903 27.067H82.0318L80.9979 21.3582C80.8389 20.5783 80.7255 19.6567 80.6582 18.5935L80.6004 19.145L80.3039 21.3879L79.2986 27.067H76.8406L74.9246 16.1714H76.8549L77.8021 22.3197C77.8938 22.9357 78.0165 23.9022 78.1707 25.2188C78.2913 23.8874 78.4048 22.9357 78.5108 22.3645L79.6387 16.1714H81.6777L82.8056 22.3645C82.9551 23.2837 83.073 24.2401 83.1597 25.2335C83.3285 23.7628 83.4515 22.7918 83.5289 22.3197L84.4469 16.1714H86.3918L84.4903 27.067Z"
                fill="currentColor"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M95.7473 27.067H93.3467L90.0785 19.1745H89.9845C90.105 19.7757 90.1655 20.4221 90.1655 21.1123V27.067H88.3435V16.1714H90.7583L93.9973 23.9816H94.0771C93.9854 23.4599 93.9399 22.839 93.9399 22.1184V16.1714H95.7473V27.067Z"
                fill="currentColor"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M105.015 21.3581H105.391C105.873 21.3581 106.232 21.2216 106.468 20.9481C106.704 20.675 106.822 20.2278 106.822 19.6069C106.822 19.0357 106.714 18.6119 106.497 18.336C106.28 18.0604 105.945 17.9226 105.492 17.9226H105.015V21.3581ZM108.811 19.5247C108.811 20.6827 108.527 21.5706 107.961 22.1893C107.395 22.8077 106.576 23.1168 105.506 23.1168H105.015V27.067H103.048V16.1714H105.731C107.784 16.1714 108.811 17.2888 108.811 19.5247Z"
                fill="currentColor"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M114.76 22.521L114.196 19.9871C114.163 19.833 114.087 19.4466 113.969 18.8282C113.851 18.2095 113.779 17.7958 113.755 17.5874C113.693 18.0146 113.613 18.4854 113.517 18.9995C113.42 19.5139 113.17 20.6875 112.765 22.521H114.76ZM115.737 27.0672L115.129 24.3173H112.374L111.767 27.0672H109.8L112.418 16.1265H115.086L117.71 27.0672H115.737Z"
                fill="currentColor"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M122.272 27.067H120.313V17.9524H118.288V16.1714H124.297V17.9524H122.272V27.067Z"
                fill="currentColor"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M128.279 21.0826H128.786C129.248 21.0826 129.581 20.9476 129.783 20.6766C129.986 20.4059 130.087 20.002 130.087 19.4652C130.087 18.9091 129.98 18.5128 129.765 18.2765C129.551 18.0408 129.219 17.9226 128.771 17.9226H128.279V21.0826ZM128.279 22.8267V27.067H126.313V16.1714H128.887C129.976 16.1714 130.78 16.4372 131.298 16.9687C131.816 17.5003 132.075 18.313 132.075 19.4056C132.075 20.7821 131.624 21.7433 130.723 22.29L132.755 27.067H130.651L128.981 22.8267H128.279Z"
                fill="currentColor"
            />
            <mask
                id="mask1_dp"
                style={{ maskType: 'luminance' }}
                maskUnits="userSpaceOnUse"
                x="134"
                y="16"
                width="8"
                height="12"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M134.28 16H141.879V27.216H134.28V16Z"
                    fill="white"
                />
            </mask>
            <g mask="url(#mask1_dp)">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M136.29 21.6044C136.29 22.8465 136.442 23.793 136.745 24.4436C137.049 25.0946 137.495 25.4202 138.083 25.4202C139.269 25.4202 139.862 24.1481 139.862 21.6044C139.862 19.0604 139.274 17.7883 138.097 17.7883C137.5 17.7883 137.049 18.1127 136.745 18.7609C136.442 19.4097 136.29 20.3574 136.29 21.6044ZM141.879 21.6045C141.879 23.4079 141.551 24.7942 140.896 25.7631C140.24 26.7317 139.302 27.216 138.083 27.216C136.858 27.216 135.919 26.733 135.263 25.7668C134.608 24.8003 134.28 23.4079 134.28 21.5895C134.28 19.7857 134.608 18.4034 135.263 17.4419C135.919 16.4809 136.864 16 138.097 16C139.317 16 140.252 16.4831 140.903 17.4495C141.554 18.4157 141.879 19.8008 141.879 21.6045Z"
                    fill="currentColor"
                />
            </g>
            <mask
                id="mask2_dp"
                style={{ maskType: 'luminance' }}
                maskUnits="userSpaceOnUse"
                x="144"
                y="16"
                width="5"
                height="12"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M144.308 16.1714H149V27.067H144.308V16.1714Z"
                    fill="white"
                />
            </mask>
            <g mask="url(#mask2_dp)">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M144.308 27.067V16.1714H146.274V25.2934H149V27.067H144.308Z"
                    fill="currentColor"
                />
            </g>
        </svg>
    );
}
